import type { ItemList, VideoGallery } from 'schema-dts';

import { generateBreadcrumbListSchema } from './breadcrumbListGenerator';
import { generateContactPointSchema } from './contactPointGenerator';
import { generateCopyrightSchema } from './copyrightGenerator';
import { generateEnhancedWebSiteSchema } from './enhancedWebSiteGenerator';
import { generateFAQSchema } from './faqSchemaGenerator';
import { generateItemListSchema } from './itemListSchemaGenerator';
import { generateLegalDocumentSchema } from './legalDocumentGenerator';
import { generateSiteNavigationSchema } from './siteNavigationGenerator';
import type { SchemaInput } from './types/common';
import type {
    BreadcrumbListInput,
    BreadcrumbListResult,
    ContactPointInput,
    ContactPointResult,
    CopyrightInput,
    CopyrightResult,
    EnhancedWebSiteInput,
    EnhancedWebSiteResult,
    FAQInput,
    FAQResult,
    LegalDocumentInput,
    LegalDocumentResult,
    SiteNavigationInput,
    SiteNavigationResult,
} from './types/company';
import type { ItemListSchemaConfig, NonStringThing } from './types/itemList';
import type { BaseVideoItem, VideoContainer, VideoSchemaGeneratorProps } from './types/video';
import type { Result } from './utils';
import { fail, ok } from './utils';
import { generateVideoCollectionSchema } from './videoSchemaGenerator';

/**
 * Constants for supported schema types in the unified generator.
 */
export const SCHEMA_TYPES = {
    CONTACT_POINT: 'contactPoint',
    BREADCRUMB_LIST: 'breadcrumbList',
    ENHANCED_WEBSITE: 'enhancedWebsite',
    ITEM_LIST: 'itemList',
    VIDEO_COLLECTION: 'videoCollection',
    LEGAL_DOCUMENT: 'legalDocument',
    SITE_NAVIGATION: 'siteNavigation',
    COPYRIGHT: 'copyright',
    FAQ: 'faq',
} as const;

/**
 * Union type of all supported schema type constants.
 */
export type SchemaType = (typeof SCHEMA_TYPES)[keyof typeof SCHEMA_TYPES];

/**
 * Mapped type that resolves schema type to its corresponding input type.
 *
 * @template T - Schema type constant
 */
export type SchemaTypeToInput<T extends SchemaType> = T extends 'contactPoint'
    ? ContactPointInput
    : T extends 'breadcrumbList'
      ? BreadcrumbListInput
      : T extends 'enhancedWebsite'
        ? EnhancedWebSiteInput
        : T extends 'itemList'
          ? ItemListSchemaConfig<SchemaInput>
          : T extends 'videoCollection'
            ? VideoSchemaGeneratorProps<VideoContainer<BaseVideoItem>>
            : T extends 'legalDocument'
              ? LegalDocumentInput
              : T extends 'siteNavigation'
                ? SiteNavigationInput
                : T extends 'copyright'
                  ? CopyrightInput
                  : T extends 'faq'
                    ? FAQInput
                    : never;

/**
 * Mapped type that resolves schema type to its corresponding output type.
 *
 * @template T - Schema type constant
 */
export type SchemaTypeToOutput<T extends SchemaType> = T extends 'contactPoint'
    ? Result<ContactPointResult, unknown>
    : T extends 'breadcrumbList'
      ? Result<BreadcrumbListResult, unknown>
      : T extends 'enhancedWebsite'
        ? Result<EnhancedWebSiteResult, unknown>
        : T extends 'itemList'
          ? NonStringThing | null
          : T extends 'videoCollection'
            ? ItemList | VideoGallery | null
            : T extends 'legalDocument'
              ? Result<LegalDocumentResult, unknown>
              : T extends 'siteNavigation'
                ? Result<SiteNavigationResult, unknown>
                : T extends 'copyright'
                  ? Result<CopyrightResult, unknown>
                  : T extends 'faq'
                    ? Result<FAQResult, unknown>
                    : never;

/**
 * Common options for schema generation across all generators.
 */
export interface GenerationOptions {
    useCache?: boolean;
    validateInput?: boolean;
    includeDefaults?: boolean;
    organizationName?: string;
}

/**
 * Universal schema generator that routes to appropriate type-specific generators.
 *
 * @template T - Schema type constant
 * @param schemaType - The type of schema to generate
 * @param input - Input data for the specific schema type
 * @param options - Optional generation settings
 * @returns Generated schema object or result based on schema type
 *
 * @example
 * ```typescript
 * const faqSchema = generateSchema('faq', {
 *   title: 'Frequently Asked Questions',
 *   questions: [{ question: 'What?', answer: 'This.' }]
 * });
 * ```
 */
export function generateSchema<T extends SchemaType>(
    schemaType: T,
    input: SchemaTypeToInput<T>,
    options?: GenerationOptions,
): SchemaTypeToOutput<T> {
    switch (schemaType) {
        case 'contactPoint':
            return generateContactPointSchema(input as ContactPointInput, options) as SchemaTypeToOutput<T>;

        case 'breadcrumbList':
            return generateBreadcrumbListSchema(input as BreadcrumbListInput, options) as SchemaTypeToOutput<T>;

        case 'enhancedWebsite':
            return generateEnhancedWebSiteSchema(input as EnhancedWebSiteInput, options) as SchemaTypeToOutput<T>;

        case 'itemList':
            return generateItemListSchema(input as ItemListSchemaConfig<SchemaInput>) as SchemaTypeToOutput<T>;

        case 'videoCollection': {
            const videoResult = generateVideoCollectionSchema(
                input as VideoSchemaGeneratorProps<VideoContainer<BaseVideoItem>>,
            );
            if (videoResult && typeof videoResult === 'string') {
                try {
                    return JSON.parse(videoResult) as SchemaTypeToOutput<T>;
                } catch {
                    return null as SchemaTypeToOutput<T>;
                }
            }
            return null as SchemaTypeToOutput<T>;
        }

        case 'legalDocument': {
            const legalResult = generateLegalDocumentSchema(input as LegalDocumentInput, options);
            if (legalResult && typeof legalResult === 'object' && 'schema' in legalResult) {
                return ok(legalResult.schema) as SchemaTypeToOutput<T>;
            }
            return fail(new Error('Invalid legal document result')) as SchemaTypeToOutput<T>;
        }

        case 'siteNavigation': {
            const navResult = generateSiteNavigationSchema(input as SiteNavigationInput, options);
            if (navResult && typeof navResult === 'object' && 'schema' in navResult) {
                return ok(navResult.schema) as SchemaTypeToOutput<T>;
            }
            return fail(new Error('Invalid site navigation result')) as SchemaTypeToOutput<T>;
        }

        case 'copyright': {
            const copyrightResult = generateCopyrightSchema(input as CopyrightInput, options);
            if (copyrightResult && typeof copyrightResult === 'object' && 'schema' in copyrightResult) {
                return ok(copyrightResult.schema) as SchemaTypeToOutput<T>;
            }
            return fail(new Error('Invalid copyright result')) as SchemaTypeToOutput<T>;
        }

        case 'faq':
            return generateFAQSchema(input as FAQInput, options) as SchemaTypeToOutput<T>;

        default:
            throw new Error(`Unsupported schema type: ${schemaType}`);
    }
}

/**
 * Type guard to check if a string is a valid schema type.
 *
 * @param type - String to validate as schema type
 * @returns True if type is valid schema type, false otherwise
 */
export function isValidSchemaType(type: string): type is SchemaType {
    return Object.values(SCHEMA_TYPES).includes(type as SchemaType);
}

/**
 * Returns array of all available schema types.
 *
 * @returns Read-only array of schema type constants
 */
export function getAvailableSchemaTypes(): readonly SchemaType[] {
    return Object.values(SCHEMA_TYPES);
}

/**
 * Batch generates multiple schemas with different types in a single call.
 *
 * @template T - Record type with schema configurations
 * @param schemas - Object mapping keys to schema type and input configurations
 * @param globalOptions - Optional settings applied to all schema generation
 * @returns Object mapping same keys to generated schema results
 *
 * @example
 * ```typescript
 * const results = generateSchemas({
 *   faq: { type: 'faq', input: faqData },
 *   breadcrumbs: { type: 'breadcrumbList', input: breadcrumbData }
 * });
 * ```
 */
export function generateSchemas<T extends Record<string, { type: SchemaType; input: unknown }>>(
    schemas: T,
    globalOptions?: GenerationOptions,
): Record<keyof T, unknown> {
    const results = {} as Record<keyof T, unknown>;

    for (const [key, schema] of Object.entries(schemas)) {
        try {
            results[key as keyof T] = generateSchema(
                schema.type,
                schema.input as SchemaTypeToInput<SchemaType>,
                globalOptions,
            );
        } catch (error) {
            results[key as keyof T] = fail(
                error instanceof Error ? error : new Error(`Failed to generate schema for ${key}`),
            );
        }
    }

    return results;
}
